import type { ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import { useTranslation } from 'react-i18next';
import { PartnerPicker, usePermissions } from '@eis/patterns';
import { insurancePermissions as p } from '../../../permissions';
import {
  canAdd,
  canRemove,
  roleLabel,
  type PartnerFormValues,
  type PartnerRefusal,
  type PartnerRoleRule,
} from '../model/partners';

/**
 * The partner inputs a pinned tarif describes (SDD-011 P-8).
 *
 * One input group per role the version allows, as many inputs as the role admits — GENERATED
 * from `partnerRoles`, never written down here, which is what makes it right for a policy pinned
 * to a version this build has never seen.
 *
 * Since SDD-014 the input is the PICKER (R-15): search-as-you-type over the Partner service's live
 * list, yielding the service's code as the LINK. A persona without `partner-management.partners.view`
 * keeps the plain id field — the picker decides nothing about permissions, this module does. The
 * role's `partnerTypes` are handed to the picker so a partner of another type is marked, not hidden.
 *
 * On a member the policyholder role is not offered and the section says why (D7).
 */
export function PartnerSection({
  rules,
  values,
  onChange,
  refusal,
  member,
  disabled,
}: {
  readonly rules: readonly PartnerRoleRule[];
  readonly values: PartnerFormValues;
  readonly onChange: (roleErn: string, ids: readonly string[]) => void;
  /** The service's refusal, mapped onto rows and the list — shown after a submit. */
  readonly refusal: PartnerRefusal;
  readonly member: boolean;
  readonly disabled?: boolean;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const { can } = usePermissions();
  const canSearch = can(p.partners.view);

  return (
    <Stack spacing={2} component="section" aria-label={t('policies.partners.title')}>
      <Typography variant="subtitle2" component="h3">
        {t('policies.partners.title')}
      </Typography>

      {member && (
        <Alert severity="info" icon={false} sx={{ py: 0 }}>
          {t('policies.partners.inheritedPolicyholder')}
        </Alert>
      )}

      {refusal.general.map((message) => (
        <Alert key={message} severity="error" data-testid="partner-refusal">
          {message}
        </Alert>
      ))}

      {rules.map((rule) => {
        const roleErn = rule.roleErn ?? '';
        const ids = values[roleErn] ?? [];
        const label = roleLabel(rule);
        const bounds =
          rule.maxCount === undefined
            ? t('policies.partners.cardinalityOpen', { min: rule.minCount ?? 0 })
            : t('policies.partners.cardinality', { min: rule.minCount ?? 0, max: rule.maxCount });
        return (
          <Stack key={roleErn} spacing={1} component="section" aria-label={label}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {(rule.minCount ?? 0) === 0 && (rule.maxCount ?? 2) === 1
                  ? t('policies.partners.optional')
                  : bounds}
              </Typography>
            </Stack>
            {ids.map((id, row) => {
              const error = refusal.rows[`${roleErn}#${String(row)}`] ?? '';
              return (
                <Stack key={row} direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
                  <PartnerPicker
                    label={
                      ids.length > 1
                        ? `${label} ${String(row + 1)} · ${t('policies.partners.partnerId')}`
                        : `${label} · ${t('policies.partners.partnerId')}`
                    }
                    value={id}
                    canSearch={canSearch}
                    required={(rule.minCount ?? 0) > row}
                    disabled={disabled === true}
                    allowedTypes={rule.partnerTypes}
                    onChange={(code) => {
                      onChange(
                        roleErn,
                        ids.map((v, i) => (i === row ? code : v)),
                      );
                    }}
                    error={error !== ''}
                    helperText={error === '' ? (canSearch ? undefined : t('policies.partners.partnerIdHint')) : error}
                  />
                  {canRemove(rule, ids.length) && disabled !== true && (
                    <IconButton
                      size="small"
                      aria-label={t('policies.partners.remove', { role: label, position: row + 1 })}
                      onClick={() => {
                        onChange(
                          roleErn,
                          ids.filter((_, i) => i !== row),
                        );
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              );
            })}
            {canAdd(rule, ids.length) && disabled !== true && (
              <Box>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    onChange(roleErn, [...ids, '']);
                  }}
                >
                  {t('policies.partners.add', { role: label })}
                </Button>
              </Box>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
}
